'use client'

import clsx from 'clsx'
import { useEffect, useRef } from 'react'

import { Navbar, NavbarItem, NavbarSection } from '../Navbar'

type TabsProps = {
  className?: string
  scrollClassName?: string
  scrollable?: boolean // When true, tabs scroll horizontally within the component instead of widening the page
  centerActiveTab?: boolean // Scrolls the active tab toward the horizontal center when it changes
  scrollKey?: string | number // Re runs centering when this value changes
  children: React.ReactNode
}

function centerActiveTabInContainer(container: HTMLElement) {
  const activeTab = container.querySelector<HTMLElement>('[data-current="true"]')
  if (!activeTab) {
    return
  }

  const activeRect = activeTab.getBoundingClientRect()
  const containerRect = container.getBoundingClientRect()
  const targetScrollLeft =
    container.scrollLeft + (activeRect.left - containerRect.left) - (containerRect.width - activeRect.width) / 2
  const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth)
  const nextScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft))

  if (nextScrollLeft === container.scrollLeft) {
    return
  }

  container.scrollTo({ left: nextScrollLeft, behavior: 'smooth' })
}

export const Tabs = ({
  className,
  scrollClassName,
  scrollable = true,
  centerActiveTab = true,
  scrollKey,
  children,
}: TabsProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!scrollable || !centerActiveTab) {
      return
    }

    const container = scrollRef.current
    if (!container) {
      return
    }

    let frameId = 0
    const scheduleCenter = () => {
      cancelAnimationFrame(frameId)
      frameId = requestAnimationFrame(() => {
        if (!container.isConnected) {
          return
        }

        centerActiveTabInContainer(container)
      })
    }

    scheduleCenter()

    const observer = new MutationObserver(scheduleCenter)
    observer.observe(container, { attributes: true, attributeFilter: ['data-current'], subtree: true })

    return () => {
      observer.disconnect()
      cancelAnimationFrame(frameId)
    }
  }, [scrollable, centerActiveTab, scrollKey])

  const tabList = (
    <Navbar className={clsx(scrollable ? 'w-auto flex-none py-0' : undefined, className)}>
      <NavbarSection
        className={clsx(
          scrollable ? 'flex w-max flex-nowrap' : 'flex w-full',
          scrollable && '[&_a]:whitespace-nowrap [&_button]:whitespace-nowrap [&_span.relative]:shrink-0',
        )}
      >
        {children}
      </NavbarSection>
    </Navbar>
  )

  if (!scrollable) {
    return tabList
  }

  return (
    <div
      ref={scrollRef}
      className={clsx('min-w-0 overflow-x-auto overscroll-x-contain', scrollClassName)}
    >
      {tabList}
    </div>
  )
}

type TabItemProps = {
  children: React.ReactNode
  current: boolean
  to?: string
  icon?: React.ReactNode
  onClick?: () => void
}

export const TabItem = ({ children, current, to, icon, onClick }: TabItemProps) => {
  return (
    <NavbarItem
      {...(to ? { to } : {})}
      current={current}
      className="w-fit shrink-0"
      onClick={onClick}
    >
      {icon && (
        <span
          data-slot="icon"
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
      {children}
    </NavbarItem>
  )
}

// Keeps children mounted when inactive so tab state (e.g. list data) is preserved on switch.
export const TabContent = ({ children, isActive }: { children: React.ReactNode; isActive: boolean }) => {
  return (
    <div
      className="mt-4"
      hidden={!isActive}
    >
      {children}
    </div>
  )
}

export default Tabs
